import { useEffect, useState } from "react";
import FormShell from "../components/FormShell";

const RED = "#E82529";

const AIRPORTS = [
  ["NRT", "Narita (NRT) — Tokyo"],
  ["HND", "Haneda (HND) — Tokyo"],
  ["KIX", "Kansai (KIX) — Osaka / Kyoto"],
  ["ITM", "Itami (ITM) — Osaka domestic"],
  ["NGO", "Centrair (NGO) — Nagoya"],
  ["CTS", "New Chitose (CTS) — Sapporo"],
  ["FUK", "Fukuoka (FUK) — Fukuoka"],
  ["OKA", "Naha (OKA) — Okinawa"],
];
const MAIN_CODES = AIRPORTS.map(([code]) => code);
const OTHER_SENTINEL = "__OTHER__";

// Airport dropdown with an "Other airport…" free-text escape hatch.
// Persists the raw code/text via onChange; styled with the v7 .jf-field.
const AirportSelect = ({ value, onChange, testId }) => {
  const valueIsKnown = MAIN_CODES.includes(value);
  const [isOtherMode, setIsOtherMode] = useState(!!value && !valueIsKnown);
  useEffect(() => {
    if (value && MAIN_CODES.includes(value)) setIsOtherMode(false);
  }, [value]);
  const selectValue = isOtherMode ? OTHER_SENTINEL : (valueIsKnown ? value : "NRT");
  return (
    <>
      <select
        value={selectValue}
        onChange={(e) => {
          if (e.target.value === OTHER_SENTINEL) { setIsOtherMode(true); onChange(""); }
          else { setIsOtherMode(false); onChange(e.target.value); }
        }}
        className="jf-field" data-testid={testId}>
        {AIRPORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        <option value={OTHER_SENTINEL}>Other airport…</option>
      </select>
      {isOtherMode && (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="Type your airport (e.g. Fukuoka, FUK, Naha)" autoFocus
          className="jf-field" style={{ marginTop: 8 }}
          data-testid={`${testId}-other-text`} />
      )}
    </>
  );
};

const DatesPage = ({ onNext, dates, setDates }) => {
  useEffect(() => {
    if (!dates.startDate) {
      const today = new Date();
      const weekLater = new Date(today);
      weekLater.setDate(weekLater.getDate() + 7);
      setDates(d => ({
        ...d,
        startDate: today.toISOString().split('T')[0],
        endDate: weekLater.toISOString().split('T')[0]
      }));
    }
  }, []);

  const tripDays = (() => {
    if (!dates.startDate || !dates.endDate) return 0;
    const diff = Math.ceil((new Date(dates.endDate) - new Date(dates.startDate)) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  })();

  const arrivalHour = parseInt(dates.arrivalTime?.split(":")[0] || "9");
  const isLateArrival = arrivalHour >= 22 || arrivalHour < 3;
  const readyHour = isLateArrival ? 9 : Math.min(arrivalHour + 4, 21);
  const departureHour = parseInt(dates.departureTime?.split(":")[0] || "21");
  const lastHour = Math.max(departureHour - 4, 6);

  const formatHour = (h) => {
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:00 ${ampm}`;
  };

  return (
    <FormShell
      step={1}
      photo="/dates-fushimi-torii-path.jpg"
      photoPos="center 35%"
      eyebrow="WHEN ARE YOU GOING?"
      bigWord="WHEN?"
      title="When are you going?"
      subtitle="Tell us your travel dates and flight times"
      onContinue={() => onNext()}
      continueDisabled={tripDays <= 0}
    >
      {/* Dates */}
      <div className="grid grid-cols-2 gap-4" style={{ marginTop: 4 }}>
        <div>
          <label className="jf-label">Arrival date</label>
          <input type="date" value={dates.startDate} onChange={(e) => setDates(d => ({ ...d, startDate: e.target.value }))}
            className="jf-field" data-testid="start-date-input" />
        </div>
        <div>
          <label className="jf-label">Departure date</label>
          <input type="date" value={dates.endDate} onChange={(e) => setDates(d => ({ ...d, endDate: e.target.value }))} min={dates.startDate}
            className="jf-field" data-testid="end-date-input" />
        </div>
      </div>

      {tripDays > 0 && (
        <div style={{ display: "flex", justifyContent: "center", padding: 12, background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4 }} data-testid="trip-days-count">
          <span className="font-mono-label" style={{ fontSize: 10, letterSpacing: "0.2em", color: RED, fontWeight: 500 }}>
            {tripDays} {tripDays === 1 ? 'day' : 'days'} in Japan
          </span>
        </div>
      )}

      {/* Flight details */}
      <div style={{ borderTop: "1px solid var(--zen-border)", paddingTop: 16 }}>
        <div className="font-mono-label" style={{ fontSize: 10, letterSpacing: "0.22em", color: "var(--zen-dim)", marginBottom: 12 }}>
          Flight details
        </div>
        <div style={{ marginBottom: 12 }}>
          <label className="jf-label">Landing airport</label>
          <AirportSelect value={dates.airport || "NRT"}
            onChange={(v) => setDates(d => ({ ...d, airport: v }))}
            testId="airport-select" />
        </div>

        {/* Flying out from a different airport? — persists dates.differentDepartureAirport,
            the field SummaryPage sends to the backend as departure_airport. */}
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", marginBottom: dates.differentDepartureAirport ? 12 : 14 }}>
          <input type="checkbox" checked={!!dates.differentDepartureAirport}
            onChange={(e) => setDates(d => ({
              ...d,
              differentDepartureAirport: e.target.checked,
              departureAirport: e.target.checked ? (d.departureAirport || d.airport || "NRT") : null,
            }))}
            style={{ marginTop: 2, accentColor: RED, width: 15, height: 15 }} data-testid="diff-airport-checkbox" />
          <span style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontSize: 13, color: "var(--zen-sub)" }}>Flying out from a different airport?</span>
        </label>

        {dates.differentDepartureAirport && (
          <div style={{ marginBottom: 12 }}>
            <label className="jf-label">Departure airport</label>
            <AirportSelect value={dates.departureAirport || dates.airport || "NRT"}
              onChange={(v) => setDates(d => ({ ...d, departureAirport: v }))}
              testId="departure-airport-select" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="jf-label">Flight lands at</label>
            <select value={dates.arrivalTime} onChange={(e) => setDates(d => ({ ...d, arrivalTime: e.target.value }))}
              className="jf-field" data-testid="arrival-time-select">
              {["06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "00", "01", "02"].map(h => {
                const hr = parseInt(h);
                const label = hr === 0 ? "12:00 AM (midnight)" : hr < 12 ? `${hr}:00 AM` : hr === 12 ? "12:00 PM" : `${hr - 12}:00 PM`;
                return <option key={h} value={`${h}:00`}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="jf-label">Flight departs at</label>
            <select value={dates.departureTime} onChange={(e) => setDates(d => ({ ...d, departureTime: e.target.value }))}
              className="jf-field" data-testid="departure-time-select">
              {["06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23"].map(h => {
                const hr = parseInt(h);
                const label = hr < 12 ? `${hr}:00 AM` : hr === 12 ? "12:00 PM" : `${hr - 12}:00 PM`;
                return <option key={h} value={`${h}:00`}>{label}</option>;
              })}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 16, padding: 14, borderRadius: 4, background: "var(--zen-s2)", border: "1px solid var(--zen-border)", display: "flex", flexDirection: "column", gap: 6 }}>
          <p style={{ fontSize: 12, color: "var(--zen-sub)", lineHeight: 1.6, margin: 0 }}>
            {isLateArrival
              ? <><span style={{ fontWeight: 600, color: "var(--zen-text)" }}>Arrival night:</span>{" "}<span style={{ fontWeight: 600, color: RED }}>Rest tonight — Day 1 starts tomorrow at 9:00 AM</span></>
              : <><span style={{ fontWeight: 600, color: "var(--zen-text)" }}>Day 1:</span>{" "}Ready to explore by <span style={{ fontWeight: 600, color: RED }}>{formatHour(readyHour)}</span></>
            }
          </p>
          <p style={{ fontSize: 12, color: "var(--zen-sub)", lineHeight: 1.6, margin: 0 }}>
            <span style={{ fontWeight: 600, color: "var(--zen-text)" }}>Last day:</span> Activities until <span style={{ fontWeight: 600, color: RED }}>{formatHour(lastHour)}</span>
            {lastHour <= 8 && <span style={{ color: "var(--zen-dim)" }}> (departure day, no activities)</span>}
          </p>
        </div>
      </div>
    </FormShell>
  );
};

export default DatesPage;
